import { useEffect, useMemo } from 'react';

import { useIsReactionsDisabledForSite } from '@confluence/comment-context/entry-points/useIsReactionsDisabledForSite.ts';
import { fg } from '@confluence/feature-gating';

import {
	useInlineReactionActions,
	useOptimisticDeletedMarkerRefs,
	useOptimisticReactionMarkerMap,
	useOptimisticResolvedMarkerRefs,
} from '../inlineReactionStore.ts';

export type InlineReactionMarkersInput = {
	contentId?: string;
	/** Server-confirmed markerRefs from `useActiveInlineData`. */
	reactionMarkerRefs: string[];
	/** Server-confirmed `{markerRef → commentId}` from `useActiveInlineData`. */
	reactionMarkerMap: Record<string, string>;
};

export type UseInlineReactionMarkersResult = {
	mergedReactionMarkerRefs: string[];
	mergedReactionMarkerMap: Record<string, string>;
	optimisticResolvedMarkerRefs: string[];
};

/**
 * Central reaction-marker reconciliation hook. Responsible for three jobs:
 *   1. Merge optimistic additions (new reaction before server confirms).
 *   2. Filter optimistic resolutions (hide resolved marker until server catches up).
 *   3. Prune the optimistic store once server data lands.
 *
 * Two places enforce the "optimistically-resolved ref stays resolved" rule:
 *   - Here (controls which markers reach the renderer as highlights).
 *   - `mapRendererAnnotationStates` in stateResolver (controls annotation active/resolved
 *     state, called via the async getState path when the comment cache may be stale).
 * Both paths must stay aligned — if this area changes, update stateResolver too.
 *
 * The initial-load vs server-confirmed prune distinction below is load-bearing;
 * see inline comments for why pruning on empty reactionMarkerRefs is wrong.
 */
export const useInlineReactionMarkers = ({
	contentId,
	reactionMarkerRefs,
	reactionMarkerMap,
}: InlineReactionMarkersInput): UseInlineReactionMarkersResult => {
	const isReactionsDisabledForSite = useIsReactionsDisabledForSite(contentId);
	const optimisticReactionMarkerMap = useOptimisticReactionMarkerMap();
	const optimisticResolvedMarkerRefs = useOptimisticResolvedMarkerRefs();
	const optimisticDeletedMarkerRefs = useOptimisticDeletedMarkerRefs();
	const {
		pruneOptimisticReactions,
		pruneOptimisticResolvedMarkerRefs,
		pruneOptimisticDeletedMarkerRefs,
	} = useInlineReactionActions();

	const optimisticResolvedMarkerRefList = useMemo(
		() => Array.from(optimisticResolvedMarkerRefs),
		[optimisticResolvedMarkerRefs],
	);

	// Drain optimistic entries once the server-confirmed map contains them so
	// the optimistic store does not grow unbounded. `pruneOptimisticReactions`
	// is itself a no-op when nothing needs pruning.
	// Also drain resolved refs once the server no longer returns them.
	useEffect(() => {
		const serverKeys = Object.keys(reactionMarkerMap);
		if (serverKeys.length > 0) {
			void pruneOptimisticReactions(serverKeys);
		}
		// Only drain resolved refs when the server has returned data — an empty
		// reactionMarkerRefs during initial load would otherwise immediately clear
		// all optimistic-resolved refs, causing the highlight to reappear when
		// server data lands.
		if (reactionMarkerRefs.length > 0) {
			void pruneOptimisticResolvedMarkerRefs(reactionMarkerRefs);
			void pruneOptimisticDeletedMarkerRefs(reactionMarkerRefs);
		}
	}, [
		reactionMarkerMap,
		reactionMarkerRefs,
		pruneOptimisticReactions,
		pruneOptimisticResolvedMarkerRefs,
		pruneOptimisticDeletedMarkerRefs,
	]);

	const mergedReactionMarkerMap = useMemo(() => {
		const hasOptimistic = Object.keys(optimisticReactionMarkerMap).length > 0;
		const hasResolved = optimisticResolvedMarkerRefs.size > 0;
		const hasDeleted = optimisticDeletedMarkerRefs.size > 0;

		if (!hasOptimistic && !hasResolved && !hasDeleted) {
			return reactionMarkerMap;
		}

		// Server map wins on key conflicts.
		const mergedMap = hasOptimistic
			? { ...optimisticReactionMarkerMap, ...reactionMarkerMap }
			: { ...reactionMarkerMap };

		for (const ref of optimisticResolvedMarkerRefs) {
			delete mergedMap[ref];
		}
		for (const ref of optimisticDeletedMarkerRefs) {
			delete mergedMap[ref];
		}

		return mergedMap;
	}, [
		reactionMarkerMap,
		optimisticReactionMarkerMap,
		optimisticResolvedMarkerRefs,
		optimisticDeletedMarkerRefs,
	]);

	const mergedReactionMarkerRefs = useMemo(() => {
		const optimisticRefs = Object.keys(optimisticReactionMarkerMap);
		const hasOptimistic = optimisticRefs.length > 0;
		const hasResolved = optimisticResolvedMarkerRefs.size > 0;
		const hasDeleted = optimisticDeletedMarkerRefs.size > 0;

		if (!hasOptimistic && !hasResolved && !hasDeleted) {
			return reactionMarkerRefs;
		}

		const set = new Set(reactionMarkerRefs);
		for (const ref of optimisticRefs) {
			set.add(ref);
		}
		// Filter out any refs that have been optimistically resolved but not yet
		// removed by the server refetch — prevents stale highlights reappearing.
		for (const ref of optimisticResolvedMarkerRefs) {
			set.delete(ref);
		}
		for (const ref of optimisticDeletedMarkerRefs) {
			set.delete(ref);
		}
		return Array.from(set);
	}, [
		reactionMarkerRefs,
		optimisticReactionMarkerMap,
		optimisticResolvedMarkerRefs,
		optimisticDeletedMarkerRefs,
	]);

	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	return isReactionsDisabledForSite && fg('confluence_hide_reactions_when_disabled')
		? {
				mergedReactionMarkerRefs: [],
				mergedReactionMarkerMap: {},
				optimisticResolvedMarkerRefs: optimisticResolvedMarkerRefList,
			}
		: {
				mergedReactionMarkerRefs,
				mergedReactionMarkerMap,
				optimisticResolvedMarkerRefs: optimisticResolvedMarkerRefList,
			};
};
